import { useState } from "react";
import { Link } from "react-router";
import { DailyBars, RangePicker } from "../../components/charts";
import { Icon, type IconName } from "../../components/icons";
import {
  Alert,
  Button,
  Card,
  CardHeader,
  LoadingBlock,
  PageHeader,
  Table,
  Tag,
  Td,
  Th,
} from "../../components/ui";
import { useCanWrite } from "../../auth/AuthProvider";
import { formatDateTime, formatNumber, formatRelative } from "../../lib/format";
import { useRiskOverview, useRiskSweep } from "../../lib/queries";
import { SEVERITY_LABEL, SEVERITY_TONE } from "../../lib/risk";
import type { RiskOverview } from "../../lib/types";

/** The sweep runs every five minutes; past this it is not running. */
const SWEEP_STALE_MS = 15 * 60_000;

/**
 * The safety system watching itself.
 *
 * The queue answers "what needs a person"; this answers "is the thing that
 * fills the queue working, and is it worth trusting". Three questions, in
 * the order an operator asks them: is the sweep alive (a queue that is
 * empty because nothing fired and one that is empty because the sweep
 * stopped look identical without this); what has been firing, day by day
 * and rule by rule; and what reviewers do with it -- the share of a
 * rule's decided cases that ended in suspension is the nearest thing to a
 * false-positive rate the platform can measure, and the number to read
 * before tuning a threshold.
 */
export function MonitorPage() {
  const canWrite = useCanWrite();
  const [days, setDays] = useState(14);
  const { data, isPending, error } = useRiskOverview(days);
  const sweep = useRiskSweep();

  if (isPending) return <LoadingBlock />;
  if (error) return <Alert>{error.message}</Alert>;

  const sweepAge = data.sweep.ranAt ? Date.now() - new Date(data.sweep.ranAt).getTime() : null;
  const sweepState: "ok" | "stale" | "never" | "failed" =
    data.sweep.error ? "failed" : sweepAge === null ? "never" : sweepAge > SWEEP_STALE_MS ? "stale" : "ok";

  return (
    <>
      <PageHeader
        title="Safety monitor"
        description="Whether the sweep is running, what it files, and what reviewers do with it."
        action={<RangePicker ranges={data.ranges} value={days} onChange={setDays} />}
      />

      {sweepState !== "ok" && (
        <Alert tone={sweepState === "failed" ? "error" : "warning"}>
          {sweepState === "never" && "The sweep has never run. Deploy the API with the cron, or run the rules by hand below."}
          {sweepState === "stale" && `The sweep last ran ${formatRelative(data.sweep.ranAt)}; it should run every five minutes. The queue is not being filled.`}
          {sweepState === "failed" && `The last sweep failed: ${data.sweep.error}`}
        </Alert>
      )}

      {/* The four numbers an operator glances at, then the working below. */}
      <div className="grid grid-cols-2 gap-px overflow-hidden rounded-lg border border-line bg-line lg:grid-cols-4">
        <Stat
          label="Waiting"
          icon="shield"
          value={data.queue.open + data.queue.reviewing}
          detail={
            data.queue.critical > 0
              ? `${data.queue.critical} critical, ${data.queue.high} high`
              : data.queue.oldestOpenAt
                ? `oldest ${formatRelative(data.queue.oldestOpenAt)}`
                : "nothing waiting"
          }
          tone={data.queue.critical > 0 ? "danger" : undefined}
        />
        <Stat
          label={`Signals, ${days}d`}
          icon="trend"
          value={data.window.signals}
          detail={`${formatNumber(data.window.occurrences)} occurrences across ${data.window.organizations} organization${data.window.organizations === 1 ? "" : "s"}`}
        />
        <Stat
          label={`Decided, ${days}d`}
          icon="check"
          value={data.window.cleared + data.window.suspended}
          detail={`${data.window.cleared} cleared · ${data.window.suspended} suspended`}
        />
        <Stat
          label="Time to decision"
          icon="clock"
          value={data.window.medianHoursToDecision === null ? null : Math.round(data.window.medianHoursToDecision)}
          unit="h"
          detail={data.window.medianHoursToDecision === null ? "no decisions yet" : "median, open to decided"}
        />
      </div>

      <div className="grid gap-4 lg:grid-cols-[2fr_1fr]">
        <Card>
          <CardHeader title="Signals filed" description="New rows per day, by severity. Repeats count on their row, not here." />
          <div className="px-5 py-4">
            <DailyBars
              points={data.series.filed.map((d) => ({
                day: d.day,
                value: d.info + d.warning + d.critical,
                parts: [
                  { label: "critical", value: d.critical },
                  { label: "warning", value: d.warning },
                  { label: "info", value: d.info },
                ],
              }))}
              format={(v) => `${formatNumber(v)} signal${v === 1 ? "" : "s"}`}
              label="Signals filed per day"
              windowLabel={`${days} days`}
              emptyLabel="Nothing filed in this window"
            />
          </div>
        </Card>
        <SweepCard data={data} onRun={canWrite ? () => sweep.mutate() : undefined} running={sweep.isPending} outcome={sweep.data?.filed ?? null} />
      </div>

      <Card>
        <CardHeader
          title="Rules"
          description="Every rule, whether or not it fired. The last column is what reviewers did with the cases each rule was on."
        />
        <Table
          head={
            <tr>
              <Th>Rule</Th>
              <Th numeric>Signals</Th>
              <Th numeric priority="md">Organizations</Th>
              <Th priority="lg">Last fired</Th>
              <Th>Outcome of decided cases</Th>
            </tr>
          }
        >
          {data.rules.map((rule) => (
            <RuleRow key={rule.kind} rule={rule} />
          ))}
        </Table>
        <p className="border-t border-line px-5 py-3 text-xs leading-5 text-fg-subtle">
          A rule whose decided cases are mostly cleared is a rule that sends reviewers to look at
          nothing — raise its threshold in <code className="font-mono">services/risk.ts</code>. One
          whose cases are mostly suspended is a candidate for auto-suspend; every rule ships with
          that off.
        </p>
      </Card>

      <Card>
        <CardHeader title="Latest signals" description="Across every organization, newest first. Refreshes every thirty seconds." />
        <ul className="divide-y divide-line">
          {data.recent.length === 0 && <li className="px-5 py-6 text-center text-sm text-fg-subtle">Nothing filed yet.</li>}
          {data.recent.map((signal) => (
            <li key={signal.id} className="flex flex-wrap items-center gap-2 px-5 py-2.5 text-sm">
              <Tag tone={SEVERITY_TONE[signal.severity]}>{SEVERITY_LABEL[signal.severity]}</Tag>
              <Link to={signal.caseId ? `/safety/${signal.caseId}` : `/organizations/${signal.organization.id}`} className="font-medium text-accent hover:underline">
                {signal.organization.name}
              </Link>
              <span className="min-w-0 flex-1 truncate text-fg-subtle" title={signal.summary}>
                {signal.summary}
              </span>
              {signal.occurrences > 1 && <Tag tone="neutral">×{signal.occurrences}</Tag>}
              <span className="text-xs text-fg-subtle" title={formatDateTime(signal.lastSeenAt)}>
                {formatRelative(signal.lastSeenAt)}
              </span>
            </li>
          ))}
        </ul>
      </Card>
    </>
  );
}

function SweepCard({
  data,
  onRun,
  running,
  outcome,
}: {
  data: RiskOverview;
  onRun?: () => void;
  running: boolean;
  outcome: number | null;
}) {
  const { sweep } = data;
  const coverage = sweep.cpu.running === 0 ? null : Math.round((sweep.cpu.sampledLast2h / sweep.cpu.running) * 100);
  return (
    <Card>
      <CardHeader
        title="The sweep"
        description="Every five minutes: the query rules, then ten running apps' CPU."
        action={
          onRun ? (
            <Button variant="secondary" size="sm" loading={running} onClick={onRun}>
              Run the rules now
            </Button>
          ) : undefined
        }
      />
      <dl className="space-y-2 px-5 py-4 text-sm">
        <Row label="Last run">{sweep.ranAt ? formatRelative(sweep.ranAt) : "never"}</Row>
        <Row label="Filed that run">{sweep.filed}</Row>
        <Row label="Rules took">{sweep.rulesMs ? `${formatNumber(sweep.rulesMs)} ms` : "—"}</Row>
        {/* Coverage says whether the mining rule can see the fleet at all: a
            server without a collector is an app that is never sampled. */}
        <Row label="CPU coverage">
          {coverage === null ? "no running apps" : `${coverage}% of ${sweep.cpu.running} running sampled in 2 h`}
        </Row>
        {sweep.cpu.oldestSampleAt && <Row label="Oldest sample">{formatRelative(sweep.cpu.oldestSampleAt)}</Row>}
        {outcome !== null && (
          <Row label="Just now">{outcome === 0 ? "nothing new to file" : `${outcome} filed`}</Row>
        )}
      </dl>
    </Card>
  );
}

function RuleRow({ rule }: { rule: RiskOverview["rules"][number] }) {
  const cleared = rule.decided - rule.suspended;
  const suspendedShare = rule.decided > 0 ? Math.round((rule.suspended / rule.decided) * 100) : null;
  return (
    <tr className={rule.signals === 0 ? "text-fg-subtle" : undefined}>
      <Td>
        <div className="flex flex-wrap items-center gap-2">
          <Tag tone={SEVERITY_TONE[rule.severity]}>{SEVERITY_LABEL[rule.severity]}</Tag>
          <span className={rule.signals === 0 ? undefined : "text-fg"}>{rule.title}</span>
        </div>
        <p className="mt-0.5 font-mono text-[11px] text-fg-subtle">{rule.kind}</p>
      </Td>
      <Td numeric>
        {formatNumber(rule.signals)}
        {rule.occurrences > rule.signals && (
          <span className="text-xs text-fg-subtle"> · {formatNumber(rule.occurrences)} times</span>
        )}
      </Td>
      <Td numeric priority="md">{formatNumber(rule.organizations)}</Td>
      <Td priority="lg" className="whitespace-nowrap">
        {rule.lastSeenAt ? formatRelative(rule.lastSeenAt) : "never"}
      </Td>
      <Td>
        {rule.decided === 0 ? (
          <span className="text-fg-subtle">none decided</span>
        ) : (
          <div className="flex items-center gap-2">
            {/* One bar, two colours: what share of this rule's cases a
                person confirmed. Numbers beside it because a bar alone
                hides a sample of one. */}
            <div className="flex h-1.5 w-24 overflow-hidden rounded bg-raised" aria-hidden="true">
              <div className="bg-danger" style={{ width: `${suspendedShare}%` }} />
            </div>
            <span className="text-xs tabular-nums text-fg-subtle">
              {rule.suspended} suspended · {cleared} cleared
            </span>
          </div>
        )}
      </Td>
    </tr>
  );
}

function Stat({
  label,
  value,
  unit,
  detail,
  icon,
  tone,
}: {
  label: string;
  value: number | null;
  unit?: string;
  detail: string;
  icon: IconName;
  tone?: "danger";
}) {
  return (
    <div className="bg-surface px-4 py-3.5 sm:px-5 sm:py-4">
      <div className="flex items-start justify-between gap-2 sm:gap-3">
        <p className="min-w-0 text-sm font-medium text-fg-subtle">{label}</p>
        <span className="grid size-8 shrink-0 place-items-center rounded-md border border-line bg-raised text-fg-subtle">
          <Icon name={icon} className="size-4" />
        </span>
      </div>
      <p className={`mt-2 text-2xl font-semibold tracking-tight tabular-nums sm:text-3xl ${tone === "danger" ? "text-danger" : ""}`}>
        {value === null ? "—" : formatNumber(value)}
        {value !== null && unit ? <span className="text-base font-normal text-fg-subtle"> {unit}</span> : null}
      </p>
      <p className="mt-1 text-xs leading-5 text-fg-subtle">{detail}</p>
    </div>
  );
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <dt className="text-fg-subtle">{label}</dt>
      <dd className="text-right text-fg">{children}</dd>
    </div>
  );
}
